import useStateValue from "frontend/state/value";
import tracking from "frontend/tracking";
import { useCallback, useEffect, useState } from "react";
import consts from "shared/consts";
import { useDebounce } from "usehooks-ts";
import style from "./templates-modal.module.css";
import useCanvasTemplates from "../../hooks/use-canvas-templates";
import { CanvasTagId, CanvasTemplateMinimal, TemplatePermission } from "shared/datamodel/schemas/canvas-template";
import useModalDeepLink from "frontend/hooks/use-modal-deep-link";
import { RESET } from "jotai/utils";
import { ModalType } from "frontend/modals/modal-manager";
import { SELECTED_CATEGORY } from "./templates-wrapper";
import SmartTemplateModal from "../smart-template-modal";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import EditTemplateModal from "./edit-template-modal";
import DeleteTemplateModal from "./delete-template-modal";
import { cleanAccountName } from "shared/util/utils";
import useTrackSearchTerm from "frontend/hooks/use-track-search-term";
import { AccountCreatedFromSource, PlanFeatureName } from "shared/datamodel/schemas";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { TemplatesModalUI } from "./components/templates-modal-ui-v2";
import { Arrow } from "frontend/ui-components/svg-shapes";

export type CoverType = "emptyState" | "upgradeScreen" | "noSearchResults";

const syncedTemplatesSectionId = Number.parseInt(process.env.SYNCED_TEMPLATES_TAG_ID ?? "1006");

export function SmartTemplateModalWrapper({
  documentId,
  template,
  onDismiss,
  onCreate,
}: {
  documentId: string;
  template: any;
  onDismiss: () => void;
  onCreate: (template: any) => void;
}) {
  return (
    <div className={style.previewContainer}>
      <div className={style.closePreview} onClick={onDismiss}>
        <Arrow direction="left" stroke={"#113357"} scale={0.8} />
        <span>Back</span>
      </div>
      <SmartTemplateModal template={template} handleCreatedTemplate={onCreate} documentId={documentId} />
    </div>
  );
}

export default function TemplatesModal({
  onDismiss,
  onChooseTemplate,
  showUpgradeCTA,
  onUpgradeCTAClick,
  currentCanvasDocumentId,
  onCreateTemplate,
}: {
  onDismiss: () => void;
  onChooseTemplate: (template: CanvasTemplateMinimal) => void;
  showUpgradeCTA?: boolean;
  onUpgradeCTAClick: (source: string) => void;
  currentCanvasDocumentId?: string;
  onCreateTemplate?: () => void;
}) {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const [{ user }] = useStateValue();
  const [previewTemplate, setPreviewTemplate] = useState<CanvasTemplateMinimal | null>(null);
  const [smartTemplate, setSmartTemplate] = useState<CanvasTemplateMinimal | null>(null);
  const [editTemplate, setEditTemplate] = useState<CanvasTemplateMinimal | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState<{ id: CanvasTagId; name: string }>({
    id: consts.ALL_TEMPLATES_TAG_ID,
    name: "All Templates",
  });
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  const { sections, tags } = useTemplates({ searchTerm: debouncedSearchTerm, selectedTag: selectedTag.id });
  const { setModalDeepLink, nameFromUrl } = useModalDeepLink();

  const isNewBoard = !currentCanvasDocumentId;

  const createTemplateEnabled = usePlanFeatureValue(PlanFeatureName.CREATE_TEMPLATE_ENABLED);

  const isSearchEmptyState = searchTerm && sections[0]?.templates.length === 0;

  const onDismissWrapper = useCallback(() => {
    setModalDeepLink({ modalType: RESET });
    onDismiss();
  }, [onDismiss, setModalDeepLink]);

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.TEMPLATES, "templates-modal-shown");
  }, []);

  useTrackSearchTerm(searchTerm, sections);

  useEffect(() => {
    const setSelectedTagByDeepLink = () => {
      if (nameFromUrl) {
        const nameWithoutPrefix = nameFromUrl.replace(SELECTED_CATEGORY, "").trimStart();
        const tag = tags.find((tag) => tag.name.toLowerCase() === nameWithoutPrefix);
        tag?.id && setSelectedTag({ id: tag.id, name: tag.name });
      }
    };
    setSelectedTagByDeepLink();
  }, [tags?.length, nameFromUrl]);

  const chooseTemplate = useCallback(
    (template: CanvasTemplateMinimal) => {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "new_template_selected_from_modal",
        template.id,
        user?.account?.id,
        user?.id
      );
      if (template.alias === "timeline" && !isNewBoard) {
        // TODO: support timeline on new boards as well
        // currently we don't support timeline on new boards because we first need to create a canvas
        // and them connect it to an integration, but now the order is reversed
        // so it needs a refactor
        setSmartTemplate(template);
      } else {
        onChooseTemplate(structuredClone(template));
      }
    },
    [user, isNewBoard, onChooseTemplate]
  );

  const onPreviewTemplateHandler = useCallback(
    (template: CanvasTemplateMinimal) => {
      setPreviewTemplate(template);
      setModalDeepLink({ modalType: ModalType.TemplateLibrary, id: template.id, name: template.name });
    },
    [setModalDeepLink]
  );

  const onTemplateClick = useCallback(
    (template: CanvasTemplateMinimal) => {
      if (template.upgradeRequired) {
        onPreviewTemplateHandler(template);
      } else {
        chooseTemplate(template);
      }
    },
    [onPreviewTemplateHandler, chooseTemplate]
  );

  const onSearch = useCallback((searchTerm: string) => {
    setSelectedTag({ id: consts.ALL_TEMPLATES_TAG_ID, name: "All Templates" });
    setSearchTerm(searchTerm);
  }, []);

  const onSeeAllClick = useCallback((tagId: number, tagName: string) => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.TEMPLATES, "templates_modal_tag_see_all_clicked", tagId);
    setSelectedTag({ id: tagId, name: tagName });
    setSearchTerm("");
  }, []);

  const onTagClicked = useCallback(
    (tagId: CanvasTagId, tagName: string) => {
      tracking.trackEvent(consts.TRACKING_CATEGORY.TEMPLATES, "templates_modal_tag_clicked", tagId);
      setSelectedTag({ id: tagId, name: tagName });
      setSearchTerm("");
      tagId === consts.ALL_TEMPLATES_TAG_ID
        ? setModalDeepLink({ modalType: ModalType.TemplateLibrary })
        : setModalDeepLink({ modalType: ModalType.TemplateLibrary, variant: SELECTED_CATEGORY, name: tagName });
    },
    [setModalDeepLink]
  );

  const updateModalDeepLink = useCallback(() => {
    selectedTag.id === consts.ALL_TEMPLATES_TAG_ID
      ? setModalDeepLink({ modalType: ModalType.TemplateLibrary })
      : setModalDeepLink({ modalType: ModalType.TemplateLibrary, variant: SELECTED_CATEGORY, name: selectedTag.name });
  }, [selectedTag, setModalDeepLink]);

  const onClosePreview = useCallback(() => {
    setPreviewTemplate(null);
    updateModalDeepLink();
  }, [updateModalDeepLink]);

  const getCoverTypeToShow = useCallback((): CoverType | null => {
    if (
      !createTemplateEnabled &&
      [consts.ACCOUNT_TEMPLATES_TAG_ID, consts.MY_TEMPLATES_TAG_ID].includes(Number(selectedTag.id))
    ) {
      return "upgradeScreen";
    } else {
      const myTemplates = sections.find((section) => section.id === consts.MY_TEMPLATES_TAG_ID);
      const accountTemplates = sections.find((section) => section.id === consts.ACCOUNT_TEMPLATES_TAG_ID);
      if (myTemplates && myTemplates.templates.length === 0 && selectedTag.id === consts.MY_TEMPLATES_TAG_ID) {
        return "emptyState";
      }
      if (
        accountTemplates &&
        accountTemplates.templates.length === 0 &&
        selectedTag.id === consts.ACCOUNT_TEMPLATES_TAG_ID
      ) {
        return "emptyState";
      }
      if (isSearchEmptyState) {
        return "noSearchResults";
      }
    }
    return null;
  }, [createTemplateEnabled, selectedTag, sections, isSearchEmptyState]);

  // Render smart template modal
  if (smartTemplate) {
    if (!currentCanvasDocumentId) {
      return null;
    }
    return (
      <SmartTemplateModalWrapper
        documentId={currentCanvasDocumentId}
        template={smartTemplate}
        onDismiss={() => setSmartTemplate(null)}
        onCreate={(template) => {
          onChooseTemplate(structuredClone(template));
        }}
      />
    );
  }

  // Render delete template modal
  if (deleteTemplateId) {
    return (
      <DeleteTemplateModal
        onDismiss={() => {
          setDeleteTemplateId(null);
          updateModalDeepLink();
        }}
        templateId={deleteTemplateId}
      />
    );
  }

  // Render edit template modal
  if (editTemplate) {
    return (
      <EditTemplateModal
        onDismiss={() => {
          setEditTemplate(null);
          updateModalDeepLink();
        }}
        template={editTemplate}
      />
    );
  }

  const shouldShowSyncedOnTop = user?.accountSource === AccountCreatedFromSource.Monday || isCimReorderEnabled;
  const accountName = user?.account?.name ?? "Account";

  // Render main UI
  return (
    <TemplatesModalUI
      sections={sections}
      tags={tags}
      searchTerm={searchTerm}
      selectedTag={selectedTag}
      previewTemplate={previewTemplate}
      coverType={getCoverTypeToShow()}
      showUpgradeCTA={showUpgradeCTA}
      isCimReorderEnabled={isCimReorderEnabled}
      syncedTemplatesSectionId={syncedTemplatesSectionId}
      shouldShowSyncedOnTop={shouldShowSyncedOnTop}
      accountName={accountName}
      onSearch={onSearch}
      onTagClicked={onTagClicked}
      onTemplateClick={onTemplateClick}
      onPreviewTemplate={onPreviewTemplateHandler}
      onSeeAllClick={onSeeAllClick}
      onUpgradeCTAClick={onUpgradeCTAClick}
      onDismiss={onDismissWrapper}
      onClosePreview={onClosePreview}
      onChooseTemplateFromPreview={chooseTemplate}
      onUpgradeModalShown={() => setModalDeepLink({ modalType: RESET })}
      onEditTemplate={setEditTemplate}
      onDeleteTemplate={setDeleteTemplateId}
      onCreateTemplate={onCreateTemplate}
    />
  );
}

export type TemplatesSection = {
  id: number;
  title: string;
  templates: CanvasTemplateMinimal[];
  showSeeAll?: boolean;
};

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function filterTemplatesBySearchAndTag(
  templates: CanvasTemplateMinimal[] | undefined,
  searchTerm: string,
  selectedTag: CanvasTagId
) {
  if (!templates) return [];
  if (!searchTerm) {
    return templates;
  }

  const safeSearchTerm = escapeRegExp(searchTerm);
  const searchTermRegex = new RegExp(safeSearchTerm, "i");
  const searchTermTrimmedRegex = new RegExp(safeSearchTerm.replace(/\s+/g, ""), "i");

  // Split matches into title matches and description matches
  const titleMatches: CanvasTemplateMinimal[] = [];
  const descriptionMatches: CanvasTemplateMinimal[] = [];

  for (const template of templates) {
    // monday org chart special case handling

    const isMondayOrgChartTemplate =
      process.env.NEXT_ENV == "production" &&
      process.env.MONDAY_ORGCHART_TEMPLATE_ID &&
      template.id.match(process.env.MONDAY_ORGCHART_TEMPLATE_ID) &&
      selectedTag !== syncedTemplatesSectionId;

    if (isMondayOrgChartTemplate) {
      continue;
    }

    if (searchTermRegex.test(template.name) || searchTermTrimmedRegex.test(template.name)) {
      titleMatches.push(template);
    } else if (
      template.description &&
      (searchTermRegex.test(template.description) || searchTermTrimmedRegex.test(template.description))
    ) {
      descriptionMatches.push(template);
    }
  }

  // Combine matches with title matches first
  return [...titleMatches, ...descriptionMatches];
}

export function useTemplates({ searchTerm, selectedTag }: { searchTerm: string; selectedTag: CanvasTagId }) {
  const [{ user }] = useStateValue();
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const { templates, tags = [] } = useCanvasTemplates();
  const filteredTemplates = filterTemplatesBySearchAndTag(templates, searchTerm, selectedTag);
  const allTags = tags.filter((tag) => tag.templates.length > 0);
  const myTemplates = filteredTemplates.filter(
    (template) => template.isOwner && template.permission === TemplatePermission.Private
  );
  const accountTemplates = filteredTemplates.filter(
    (template) => template.isAccountTemplate && template.permission === TemplatePermission.Account
  );

  const myTemplatesSection = {
    id: consts.MY_TEMPLATES_TAG_ID,
    title: "My Templates",
    templates: myTemplates,
  } as TemplatesSection;

  const accountTemplatesSection = {
    id: consts.ACCOUNT_TEMPLATES_TAG_ID,
    title: `${cleanAccountName(user?.account?.name ?? "Account")} Templates`,
    templates: accountTemplates,
  } as TemplatesSection;

  function getAllTemplatesSections(): TemplatesSection[] {
    const tagSections = tags.map((tag) => {
      // BUG: this filters templates without tags
      const templates = filteredTemplates.filter((template) => tag.templates.includes(template.id));
      const exmapleTemplates = templates.filter((index) => tag.exampleTemplates?.includes(Number.parseInt(index.id)));
      const templatesToShow = exmapleTemplates.length >= 3 ? exmapleTemplates : templates;
      const shownTemplates = searchTerm ? templates : templatesToShow.slice(0, isCimReorderEnabled ? 5 : 3);

      return {
        id: tag.id,
        title: tag.name,
        templates: shownTemplates,
        showSeeAll: templates.length > 3,
      } as TemplatesSection;
    });
    return tagSections.filter((section) => section.templates.length > 0);
  }

  function getTagSection(): TemplatesSection[] {
    if (selectedTag === consts.ACCOUNT_TEMPLATES_TAG_ID) {
      return [accountTemplatesSection];
    }
    if (selectedTag === consts.MY_TEMPLATES_TAG_ID) {
      return [myTemplatesSection];
    }
    const tag = tags.find((tag) => tag.id === selectedTag);
    const templates = filteredTemplates.filter((template) => tag?.templates.includes(template.id));
    return [
      {
        id: tag?.id || -1,
        title: tag?.name || "",
        templates,
        showSeeAll: false,
      } as TemplatesSection,
    ];
  }

  function getUniqueSectionWithFilteredUniqueTemplatesBySearch(
    sections: TemplatesSection[],
    orderedTemplates: CanvasTemplateMinimal[]
  ): TemplatesSection[] {
    const sectionTemplateIds = new Set(sections.flatMap((section) => section.templates).map((template) => template.id));

    const uniqueTemplates = orderedTemplates.filter((template) => sectionTemplateIds.has(template.id));

    return [
      {
        id: -1,
        title: "Search Results",
        templates: uniqueTemplates,
        showSeeAll: false,
      },
    ];
  }

  function getSectionsToShow(): TemplatesSection[] {
    const sectionsToShow = selectedTag === consts.ALL_TEMPLATES_TAG_ID ? getAllTemplatesSections() : getTagSection();

    if (searchTerm) {
      return getUniqueSectionWithFilteredUniqueTemplatesBySearch(
        [...sectionsToShow, myTemplatesSection, accountTemplatesSection],
        filteredTemplates
      );
    }

    return sectionsToShow;
  }

  return {
    sections: getSectionsToShow(),
    tags: allTags,
  };
}
